'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { Button } from '@repo/ui';
import { Chevron } from '@repo/ui/icons';
import { useState } from 'react';
import { WarningIcon } from '@/components/common/WarningIcon';
import { formatPreciseDuration } from '@/components/VaultPage/helpers/timelock';
import { trackVaultV2TimelocksToggled } from '@/lib/analytics/client/trackers/timelocks';
import { VaultV2RowList, VaultV2RowListRow } from '../../components/VaultV2RowList';
import { getDecodedFunctionNameLabel } from '../../VaultV2PendingTimelock/utils';
import { CAP_CHANGE_NOTICE_FUNCTIONS, CapChangeNoticeOverview } from './CapChangeNotice';
import { getPendingTimelocksForFunction, getTimelockBelowMinimumWarning, getTimelockDescription, orderTimelocks, VAULT_EXPOSURE_TIMELOCKS_DESCRIPTION, type VaultV2PendingTimelockChange, type VaultV2TimelockEntry, type VaultV2TimelocksWarning } from './utils';
import VaultV2PendingTimelocksIndicator from './VaultV2PendingTimelocksIndicator';
const VAULT_EXPOSURE_TIMELOCK_FUNCTION_NAMES = new Set<string>(Object.values(CAP_CHANGE_NOTICE_FUNCTIONS));
interface VaultV2TimelocksClientProps {
  vaultAddress: Address;
  chainId: ChainId;
  timelocks: VaultV2TimelockEntry[];
  warning: VaultV2TimelocksWarning;
  pendingTimelocks: VaultV2PendingTimelockChange[];
  /** Set when a warning check or a pending change could not be read and is left out. */
  partial: boolean;
}
const getTimelockDuration = (timelock: VaultV2TimelockEntry) => {
  if (timelock.durationSeconds) return formatPreciseDuration(timelock.durationSeconds);
  return 'No timelock';
};
const VaultV2TimelocksClient = t0 => {
  const $ = _c(26);
  const {
    vaultAddress,
    chainId,
    timelocks,
    warning,
    pendingTimelocks,
    partial
  } = t0;
  let t1;
  if ($[0] !== timelocks) {
    t1 = timelocks.map(_temp);
    $[0] = timelocks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const indicatorTimelocks = t1;
  let t2;
  if ($[2] !== timelocks) {
    const otherTimelocks = timelocks.filter(_temp2);
    t2 = orderTimelocks(otherTimelocks);
    $[2] = timelocks;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const orderedTimelocks = t2;
  const [areOtherTimelocksVisible, setAreOtherTimelocksVisible] = useState(false);
  if (timelocks.length === 0) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="text-2xs text-secondary-foreground">{VAULT_EXPOSURE_TIMELOCKS_DESCRIPTION}</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== indicatorTimelocks || $[6] !== pendingTimelocks || $[7] !== timelocks || $[8] !== warning) {
    t4 = <VaultV2RowList><CapChangeNoticeOverview timelocks={timelocks} warning={warning} pendingTimelocks={pendingTimelocks} indicatorTimelocks={indicatorTimelocks} /></VaultV2RowList>;
    $[5] = indicatorTimelocks;
    $[6] = pendingTimelocks;
    $[7] = timelocks;
    $[8] = warning;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== partial) {
    t5 = partial ? <span className="text-2xs text-secondary-foreground" data-testid="vault-v2-timelocks-partial">Some timelock warnings or pending changes could not be loaded.</span> : null;
    $[10] = partial;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <div className="gap-sm flex flex-col">{t3}{t4}{t5}</div>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== areOtherTimelocksVisible || $[16] !== chainId || $[17] !== indicatorTimelocks || $[18] !== orderedTimelocks || $[19] !== pendingTimelocks || $[20] !== vaultAddress || $[21] !== warning) {
    t7 = orderedTimelocks.length > 0 ? <div className="gap-sm mt-sm flex flex-col"><div className="flex justify-center"><Button variant="ghost" size="sm" aria-controls="vault-v2-other-timelocks" aria-expanded={areOtherTimelocksVisible} aria-label={areOtherTimelocksVisible ? "Hide other timelocks" : "See all timelocks"} onClick={() => {
          const nextVisible = !areOtherTimelocksVisible;
          setAreOtherTimelocksVisible(nextVisible);
          trackVaultV2TimelocksToggled({
            vaultAddress,
            chainId,
            isExpanded: nextVisible,
            otherTimelockCount: orderedTimelocks.length
          });
        }}>See all timelocks<Chevron className={areOtherTimelocksVisible ? "text-secondary-foreground rotate-0 transition-transform" : "text-secondary-foreground -rotate-90 transition-transform"} /></Button></div>{areOtherTimelocksVisible ? <div id="vault-v2-other-timelocks"><VaultV2RowList>{orderedTimelocks.map(timelock_1 => {
            const matchingPendingTimelocks = getPendingTimelocksForFunction(pendingTimelocks, timelock_1.functionName);
            const label = getDecodedFunctionNameLabel(timelock_1.functionName);
            const description = getTimelockDescription(timelock_1.functionName);
            const duration = getTimelockDuration(timelock_1);
            const timelockWarning = getTimelockBelowMinimumWarning(warning, timelock_1.functionName);
            return <VaultV2RowListRow key={timelock_1.functionName} label={label} description={description}>{matchingPendingTimelocks.length > 0 && <VaultV2PendingTimelocksIndicator pendingTimelocks={matchingPendingTimelocks} timelocks={indicatorTimelocks} />}{timelockWarning && <WarningIcon warnings={[timelockWarning]} isSimple={true} />}<span className="text-foreground text-xs">{duration}</span></VaultV2RowListRow>;
          })}</VaultV2RowList></div> : null}</div> : null;
    $[15] = areOtherTimelocksVisible;
    $[16] = chainId;
    $[17] = indicatorTimelocks;
    $[18] = orderedTimelocks;
    $[19] = pendingTimelocks;
    $[20] = vaultAddress;
    $[21] = warning;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t6 || $[24] !== t7) {
    t8 = <div className="flex flex-col">{t6}{t7}</div>;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
export default VaultV2TimelocksClient;
function _temp(timelock) {
  return {
    functionName: timelock.functionName,
    duration: timelock.durationSeconds
  };
}
function _temp2(timelock_0) {
  return timelock_0.functionName !== "decreaseTimelock" && timelock_0.abdicatedAt == null && !VAULT_EXPOSURE_TIMELOCK_FUNCTION_NAMES.has(timelock_0.functionName);
}